/**
 * VerdictBottomsheet — Artwork detail view (renders inside BottomSheet)
 *
 * Sections:
 * 1. Hero zone (cream bg, no card): title, price, margin pill, 3 stats
 * 2. Price Corridor (white card)
 * 3. Forensic Roast (white card, teal accent)
 * 4. Sources Expandable (white card, single dropdown)
 *
 * Kebab menu: Re-Price, Create Quote, Mark as Sold/Undo Sold, Delete
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { Verdict } from '@/types';
import type { UserProfile } from '@/types';
import { PriceCorridor } from './PriceCorridor';
import { useForensicRoast } from '@/hooks/useForensicRoast';
import { InputsPill } from './InputsPill';

const TEAL = '#3DABA5';
const RED = '#DC2626';
const CHARCOAL = '#1A1A1A';
const CREAM = '#FAF8F3';
const FONT = "'Manrope', 'Inter', -apple-system, sans-serif";

function currency(cents: number) {
  return '$' + Math.round(cents / 100).toLocaleString();
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ═════════════════════════════════════════════════════════════════
// KEBAB MENU
// ═════════════════════════════════════════════════════════════════

interface KebabMenuProps {
  isSold: boolean;
  onRePrice: () => void;
  onCreateQuote: () => void;
  onMarkSold: () => void;
  onUndoSold: () => void;
  onDelete: () => void;
}

function KebabMenu({ isSold, onRePrice, onCreateQuote, onMarkSold, onUndoSold, onDelete }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Use capture phase so we catch clicks before stopPropagation
    document.addEventListener('mousedown', handleClick, true);
    document.addEventListener('touchstart', handleClick as any, true);
    return () => {
      document.removeEventListener('mousedown', handleClick, true);
      document.removeEventListener('touchstart', handleClick as any, true);
    };
  }, [open]);

  const actions: { label: string; action: () => void; destructive?: boolean }[] = [];
  if (!isSold) {
    actions.push({ label: 'Re-Price', action: onRePrice });
  }
  actions.push({ label: 'Create Quote', action: onCreateQuote });
  if (isSold) {
    actions.push({ label: 'Undo Sold', action: onUndoSold });
  } else {
    actions.push({ label: 'Mark as Sold', action: onMarkSold });
  }
  actions.push({ label: 'Delete', action: onDelete, destructive: true });

  return (
    <div ref={menuRef} style={{ position: 'relative', zIndex: 10 }}>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          width: 36, height: 36, borderRadius: 10,
          border: '1px solid #E7E5E4', backgroundColor: 'rgba(255,255,255,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0, padding: 0,
          touchAction: 'manipulation',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="3.5" r="1.2" fill="#78716C" />
          <circle cx="8" cy="8" r="1.2" fill="#78716C" />
          <circle cx="8" cy="12.5" r="1.2" fill="#78716C" />
        </svg>
      </button>

      {open && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'white', borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '4px 0', minWidth: 160, zIndex: 100,
            animation: 'kebabPop 150ms ease-out',
            fontFamily: FONT,
          }}
        >
          {actions.map((item, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setOpen(false); item.action(); }}
              style={{
                width: '100%', padding: '10px 16px', border: 'none',
                background: 'none', cursor: 'pointer', textAlign: 'left' as const,
                fontSize: 13, fontWeight: 500,
                color: item.destructive ? RED : CHARCOAL,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════

interface VerdictBottomsheetProps {
  verdict: Verdict;
  userId?: string;
  profile?: UserProfile | null;
  onRePrice?: (verdict: Verdict) => void;
  onCreateQuote?: (verdict: Verdict) => void;
  onMarkSold?: (verdict: Verdict) => void;
  onUndoSold?: (verdict: Verdict) => void;
  onDelete?: (verdict: Verdict) => void;
  onConfirmManual?: (verdict: Verdict) => void;
}

export function VerdictBottomsheet({
  verdict, userId, profile,
  onRePrice, onCreateQuote, onMarkSold, onUndoSold, onDelete, onConfirmManual,
}: VerdictBottomsheetProps) {
  const navigate = useNavigate();
  const v = verdict;

  // Forensic roast (V2 — cache-first, no API call on view)
  const roast = useForensicRoast({ verdict: v, userId: userId ?? undefined, profile: profile ?? null });

  // Derived values (all in cents from Firestore)
  const askingPrice = v.inputs.gutPrice ?? v.calculations.ttcp;
  const floor = v.calculations.ttcp;
  const margin = askingPrice - floor;
  const isAbove = margin >= 0;
  const absMargin = Math.abs(margin);

  const effHourly = v.calculations.effectiveHourly != null
    ? Math.round(v.calculations.effectiveHourly / 100) : 0;
  const sustainability = v.calculations.sustainabilityRatio ?? 0;
  const sustainabilityDisplay = Math.round(sustainability * 10) / 10;

  const isSold = v.sold;

  // Reconciliation nudge logic: 7-29 days = subtle, 30+ days = stronger copy
  const reconcStatus = v.reconciliationStatus || (isSold ? 'reported' : 'none');
  const soldDateMs = v.soldDate ? new Date(v.soldDate).getTime() : 0;
  const daysSinceSold = soldDateMs ? Math.floor((Date.now() - soldDateMs) / 86400000) : 0;
  const isReportedStale = isSold && reconcStatus === 'reported';
  const show30DayNudge = isReportedStale && daysSinceSold >= 30;
  const show7DayOption = isReportedStale && daysSinceSold >= 7 && daysSinceSold < 30;

  // Fire-and-forget: record that the 30-day nudge was shown (for analytics)
  useEffect(() => {
    if (show30DayNudge && !v.reconciliationNudgedAt && userId) {
      const ref = doc(db, 'users', userId, 'verdicts', v.id!);
      updateDoc(ref, { reconciliationNudgedAt: new Date().toISOString() }).catch(() => {});
    }
  }, [show30DayNudge, v.reconciliationNudgedAt, v.id, userId]);

  const title = v.inputs.title || `${v.inputs.medium} ${v.inputs.width}×${v.inputs.height}`;
  const subtitle = `${v.inputs.medium} · ${v.inputs.width}" × ${v.inputs.height}"`;
  const dateStr = formatDate(v.createdAt);

  // Accent colors
  const marginColor = isAbove ? TEAL : RED;
  const sustainColor = sustainability >= 1 ? TEAL : RED;

  // Handlers
  const handleRePrice = useCallback(() => {
    if (onRePrice) onRePrice(v);
    else navigate(`/app/adjust/${v.id}`);
  }, [v, onRePrice, navigate]);

  const handleCreateQuote = useCallback(() => {
    if (onCreateQuote) onCreateQuote(v);
    else navigate(`/app/quote/new/${v.id}`, { state: { fromRoute: '/app/portfolio' } });
  }, [v, onCreateQuote, navigate]);

  return (
    <div style={{
      backgroundColor: CREAM,
      fontFamily: FONT,
    }}>
      <style>{`
        @keyframes kebabPop {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════
          HERO ZONE — flat on cream, no card
          ═══════════════════════════════════════════════════════ */}
      <div style={{ padding: '4px 20px 0' }}>
        {/* Title row + kebab */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
            <div style={{
              fontSize: 17, fontWeight: 700, color: CHARCOAL, lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
            }}>
              {title}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#78716C', marginTop: 3 }}>
              {subtitle}
            </div>
            <div style={{ fontSize: 11, color: '#A8A29E', marginTop: 2 }}>{dateStr}</div>
          </div>
          <KebabMenu
            isSold={isSold}
            onRePrice={handleRePrice}
            onCreateQuote={handleCreateQuote}
            onMarkSold={() => onMarkSold?.(v)}
            onUndoSold={() => onUndoSold?.(v)}
            onDelete={() => onDelete?.(v)}
          />
        </div>

        {/* Hero Price */}
        <div style={{ textAlign: 'center' as const, marginTop: 24 }}>
          <div style={{
            fontSize: 48, fontWeight: 800, color: CHARCOAL,
            letterSpacing: '-0.03em', lineHeight: 1,
          }}>
            {currency(askingPrice)}
          </div>
          <div style={{
            marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 14px', borderRadius: 20, backgroundColor: `${marginColor}10`,
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              {isAbove ? (
                <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke={marginColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <path d="M6 3V9M6 9L3 6M6 9L9 6" stroke={marginColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              )}
            </svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: marginColor }}>
              {isAbove ? '+' : '-'}{currency(absMargin)}
            </span>
            <span style={{ fontSize: 11, color: '#78716C' }}>
              {isAbove ? 'above floor' : 'below floor'}
            </span>
          </div>

          {/* Verdict headline */}
          <div style={{
            marginTop: 14, fontSize: 14, fontWeight: 600, color: '#57534E',
            letterSpacing: '-0.01em', textAlign: 'center' as const,
          }}>
            {sustainability >= 2.5 ? 'Strong margin. Room to negotiate.' :
             sustainability >= 1.5 ? 'Covers costs with margin to spare.' :
             sustainability >= 1.0 ? 'Covers costs. Margin is thin.' :
             "Below your floor. You're paying to work."}
          </div>
        </div>

        {/* Stats — flat on cream, thin dividers */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 18, marginBottom: 16 }}>
          <div style={{ flex: 1, textAlign: 'center' as const }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: CHARCOAL, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {currency(floor)}
            </div>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
              color: '#A8A29E', textTransform: 'uppercase' as const, marginTop: 5,
            }}>Floor</div>
          </div>
          <div style={{ width: 1, height: 28, backgroundColor: '#E7E5E4' }} />
          <div style={{ flex: 1, textAlign: 'center' as const }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: CHARCOAL, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              ${effHourly}/hr
            </div>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
              color: '#A8A29E', textTransform: 'uppercase' as const, marginTop: 5,
            }}>Hourly Rate</div>
          </div>
          <div style={{ width: 1, height: 28, backgroundColor: '#E7E5E4' }} />
          <div style={{ flex: 1, textAlign: 'center' as const }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: sustainColor, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {sustainabilityDisplay}x
            </div>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
              color: '#A8A29E', textTransform: 'uppercase' as const, marginTop: 5,
            }}>vs Target</div>
          </div>
        </div>
      </div>

      {/* ═══ SOLD BANNER ═══ */}
      {isSold && (
        <div style={{
          background: 'rgba(168, 165, 160, 0.08)',
          padding: '10px 20px',
          margin: '0 0 4px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const,
            letterSpacing: '0.04em', color: '#FFF', background: '#78716C',
            padding: '2px 8px', borderRadius: 4,
          }}>
            Sold
          </span>
          <span style={{ fontSize: 14, fontWeight: 500, color: CHARCOAL }}>
            for {currency(v.soldPrice ?? 0)}
          </span>
        </div>
      )}

      {/* ═══ 30-DAY RECEIVABLES NUDGE ═══ */}
      {show30DayNudge && (
        <div style={{
          background: CREAM, padding: 16, margin: '0 16px 4px',
          borderRadius: 12,
        }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: CHARCOAL, margin: '0 0 4px', lineHeight: 1.4 }}>
            Still waiting on payment for this piece?
          </p>
          <p style={{ fontSize: 13, color: '#78716C', margin: '0 0 8px', lineHeight: 1.45 }}>
            Reported sold {daysSinceSold} days ago — no matching deposit found yet.
          </p>
          <button
            onClick={() => onConfirmManual?.(v)}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: 13, fontWeight: 600, color: TEAL,
              cursor: 'pointer',
            }}
          >
            No bank record expected
          </button>
        </div>
      )}

      {/* ═══ 7-DAY CONFIRMED MANUAL OPTION ═══ */}
      {show7DayOption && (
        <div style={{
          background: CREAM, padding: 12, margin: '0 16px 4px',
          borderRadius: 8,
        }}>
          <p style={{ fontSize: 13, color: '#78716C', margin: 0, lineHeight: 1.45 }}>
            No matching bank deposit found after {daysSinceSold} days.
          </p>
          <button
            onClick={() => onConfirmManual?.(v)}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: 13, fontWeight: 600, color: TEAL,
              cursor: 'pointer', marginTop: 6,
            }}
          >
            No bank record expected
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          CONTENT — white cards on cream
          ═══════════════════════════════════════════════════════ */}
      <div style={{ padding: '16px 16px calc(100px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
        {/* Corridor */}
        <div style={{
          backgroundColor: 'white', borderRadius: 16,
          padding: '16px 18px 14px', border: '1px solid #F1F0EE',
        }}>
          <PriceCorridor price={askingPrice} floor={floor} />
        </div>

        {/* Forensic Roast (V2 — inline via hook) */}
        <div style={{
          backgroundColor: 'white', borderRadius: 16,
          border: '1px solid #F1F0EE', overflow: 'hidden',
        }}>
          <div style={{ height: 3, background: TEAL }} />
          <div style={{ padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ color: TEAL, fontSize: 14 }}>✦</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: TEAL, textTransform: 'uppercase' as const }}>
                TRUEPRICE'S ANALYSIS
              </span>
            </div>
            {roast.isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                {[100, 85, 60].map((w, i) => (
                  <div key={i} style={{
                    height: 12, borderRadius: 6, width: `${w}%`,
                    background: '#f0ede6',
                    animation: 'shimmer 1.5s infinite linear',
                    backgroundImage: 'linear-gradient(90deg, #f0ede6 0%, #f8f5ef 50%, #f0ede6 100%)',
                    backgroundSize: '200% 100%',
                  }} />
                ))}
              </div>
            ) : roast.analysis ? (
              <div style={{ fontSize: 14, lineHeight: 1.55, color: '#555' }}>
                {roast.analysis.split('\n\n').map((para, i) => (
                  <p key={i} style={{ margin: i > 0 ? '14px 0 0' : '0' }}>
                    {para.split(/(\*\*[^*]+\*\*|\$[\d,]+)/g).map((chunk, j) => {
                      if (chunk.startsWith('**') && chunk.endsWith('**'))
                        return <span key={j} style={{ fontWeight: 700, color: CHARCOAL }}>{chunk.slice(2, -2)}</span>;
                      if (chunk.startsWith('$'))
                        return <span key={j} style={{ fontWeight: 700, color: CHARCOAL }}>{chunk}</span>;
                      return <span key={j}>{chunk}</span>;
                    })}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Inputs Pill */}
        <InputsPill verdict={v} profile={profile} />

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5,
          paddingTop: 4, paddingBottom: 16,
        }}>
          <span style={{ fontSize: 10, color: '#C8C4BF', letterSpacing: '0.02em', fontFamily: FONT }}>
            Powered by
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#A8A29E', letterSpacing: '0.04em', fontFamily: FONT }}>
            TruePrice
          </span>
        </div>
      </div>
    </div>
  );
}
